/**
 * @file     VowLockedPriceChange.tsx
 * @purpose  The blurred amount beside "Price reduced" for anyone without VOW
 *           access. The previous price and the size of the change are VOW
 *           data (OriginalListPrice is vow_only), so the server never sends
 *           them; this draws a fixed placeholder, never the real figure, and
 *           is one button into the VOW gate (sign-in signed out, then the
 *           terms; the terms signed in).
 */
'use client';

import type { MouseEvent, SyntheticEvent } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { ListingEventProps } from '@/core/analytics/listingEvents';
import { hubColors } from '@/components/hub/hubKitStyles';
import { motionTokens } from '@/design-system/tokens/motion';
import { layoutTokens } from '@/design-system/tokens/layout';
import { useVowGate } from './useVowGate';

/** Same shape for every listing, so the blur says nothing about the cut. */
export const PRICE_CHANGE_PLACEHOLDER = '$00,000';

const stop = (e: SyntheticEvent) => e.stopPropagation();

export interface VowLockedPriceChangeProps {
  listingKey?: string;
  surface: ListingEventProps['gate_shown']['surface'];
  /** Server-known sign-in state when the caller has it; the client session otherwise. */
  isAuthenticated?: boolean;
}

export function VowLockedPriceChange({ listingKey, surface, isAuthenticated }: VowLockedPriceChangeProps) {
  const { t } = useTranslation();
  const gate = useVowGate({
    isAuthenticated,
    surface,
    listingKey,
    headline: t('listingDetail.priceTrend.gateHeadline', { defaultValue: 'See how much the price changed' }),
  });
  const label = gate.authed
    ? t('listingDetail.priceTrend.lockedAuthed', { defaultValue: 'Accept the VOW terms to see the price change' })
    : t('listingDetail.priceTrend.locked', { defaultValue: 'Sign in to see the price change' });

  const onClick = (e: MouseEvent<HTMLButtonElement>) => {
    // Cards open the listing on a click anywhere else; this press is the gate's.
    e.preventDefault();
    e.stopPropagation();
    gate.openGate('teaser_row', 'priceChange');
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        data-vow-locked-price
        aria-label={label}
        title={label}
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          m: 0,
          px: 0.5,
          border: 0,
          borderRadius: `${layoutTokens.radius.pill}px`,
          background: 'none',
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer',
          // A 44px hit area around a figure the size of the text beside it.
          '&::before': { content: '""', position: 'absolute', inset: '-10px -4px' },
          '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
          '@media (hover: hover)': { '&:hover [data-vow-placeholder]': { opacity: 0.7 } },
        }}
      >
        <Box
          component="span"
          aria-hidden
          data-vow-placeholder
          sx={{
            filter: 'blur(5px)',
            userSelect: 'none',
            pointerEvents: 'none',
            fontVariantNumeric: 'tabular-nums',
            transition: `opacity ${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`,
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          {PRICE_CHANGE_PLACEHOLDER}
        </Box>
      </Box>
      {/* Clicks inside the modal portal bubble through React to the card. */}
      {gate.modal ? <Box component="span" onClick={stop} sx={{ display: 'contents' }}>{gate.modal}</Box> : null}
    </>
  );
}
